// The header language toggle (fleet i18n standard, 2026-10-21:
// ptc-docs/coordination/plans/2026-10-21-fleet-i18n-standard.md), ported from ptc-statetax's
// reference implementation (src/components/layout/LanguageToggle.tsx) to this app's own Fluent UI
// idiom and React Query usage (plain async functions in services/api.ts, not a separate hooks
// module -- see NotificationBell.tsx for the same pattern).
//
// "No default" ruling (Christian, 2026-10-21): NO language is ever assigned by anyone. The choice
// persists twice: per BROWSER (i18next's detector caches it in localStorage, so the page paints in
// it before sign-in resolves) and per PERSON (api/user-preferences, so it follows them to another
// machine). A saved choice is applied ONCE, when the preferences GET resolves after sign-in; after
// that the toggle owns it, and a choice made before the GET resolves is never overridden by it.

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Tooltip } from '@fluentui/react-components';
import { TranslateRegular } from '@fluentui/react-icons';
import { getUserPreferences, updateLanguagePreference } from '../../services/api';

export default function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const settled = useRef(false);

  const { data: prefs } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: getUserPreferences,
  });

  const save = useMutation({
    mutationFn: updateLanguagePreference,
  });

  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es') ? 'es' : 'en';
  const next = current === 'es' ? 'en' : 'es';

  useEffect(() => {
    if (settled.current || !prefs) return;
    settled.current = true;
    const saved = prefs.language;
    if ((saved === 'en' || saved === 'es') && saved !== current) {
      i18n.changeLanguage(saved).catch(() => {
        // A failed language switch leaves the current (browser) language in place -- same
        // fallback as the toggle's own click handler below. Never left floating: an unawaited
        // changeLanguage() call is the exact race that corrupted the suite under load (see
        // LanguageToggle.test.tsx and AppHeader.i18n.test.tsx), so every call site here is
        // either awaited or has its rejection handled, never fire-and-forget.
      });
    }
  }, [prefs, current, i18n]);

  const label = current === 'es' ? t('common.language.switchToEnglish') : t('common.language.switchToSpanish');

  return (
    <Tooltip content={label} relationship="label">
      <Button
        appearance="transparent"
        aria-label={label}
        icon={<TranslateRegular />}
        style={{ color: 'var(--tl-header-fg)' }}
        onClick={() => {
          settled.current = true;
          i18n.changeLanguage(next).catch(() => {
            // Same rationale as the effect above -- handled, never floating.
          });
          // A failed save leaves this browser's choice in place; the next toggle saves again.
          save.mutate(next);
        }}
      >
        {current === 'es' ? t('common.language.es') : t('common.language.en')}
      </Button>
    </Tooltip>
  );
}
